import { rem } from 'polished'
import { Fragment, useMemo } from 'react'
import { useIntl } from 'react-intl'
import styled from 'styled-components'
import BodyText from 'LuxKit/Typography/BodyText'
import HelpPane from 'components/Common/HelpPane'
import Modal from 'LuxKit/Modal/Modal'
import VerticalSpacer from 'components/Common/Spacing/VerticalSpacer'
import Group from 'components/utils/Group'
import moment from 'moment'

const Divider = styled.hr`
  grid-column: span 2;
  width: 100%;
  height: 1px;
  margin: 0;
  background-color: ${props => props.theme.palette.neutral.default.six};
  border: none;
`

const PolicyGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  grid-row-gap: ${rem(16)};
  grid-column-gap: ${rem(24)};
  margin-top: ${rem(40)};
`
interface ExperienceRefundPolicy {
  value: number
  period?: string
  periodLabel?: string
  id: 'UP-TO-DAYS' | 'BEFORE-BOOKING-DATE' | 'CUSTOM-POLICY'
}

interface Props {
  cancellationInfo?: App.ExperienceItemCancellationInfo
  date?: string
  onSupportEmailClick?: () => void
  onSupportNumberClick?: () => void
}

function ExperienceCancellationPolicyModal(props: Props) {
  const { cancellationInfo, onSupportEmailClick, onSupportNumberClick } = props
  const intl = useIntl()

  const policies = useMemo(() => {
    const policyMap = (cancellationInfo?.refundPolicies ?? []).reduce<Record<string, Array<ExperienceRefundPolicy>>>((acc, policy) => {
      const key = policy.groupId ?? 1
      if (!acc[key]) acc[key] = []
      acc[key].push({
        value: policy.value,
        period: policy.periods[0],
        id: policy.id,
        periodLabel: policy.periodLabel,
      })
      return acc
    }, {})

    return Object.entries(policyMap)
  }, [cancellationInfo?.refundPolicies])

  const nonRefundableLabel = useMemo(() => {
    const groupedPolicies = policies.flatMap((policies) => policies[1])
    const nonRefundablePolicy = groupedPolicies.reduce<ExperienceRefundPolicy | undefined>((acc, policy) => {
      if (policy.id === 'BEFORE-BOOKING-DATE') {
        if (!acc) return policy
        return moment.duration(policy.period).asMilliseconds() < moment.duration(acc.period).asMilliseconds() ? policy : acc
      }

      return acc
    }, undefined)

    if (!nonRefundablePolicy) {
      return intl.formatMessage({
        id: 'PN6fnNJo9nszr6VfiGHe',
        defaultMessage: 'Outside of other cancellation policies',
        description: 'Default non-refundable label when no specific policy period exists',
      })
    }
    const duration = moment.duration(nonRefundablePolicy.period)

    const hours = duration.asHours()
    if (hours > 72) {
      return intl.formatMessage(
        {
          id: 'nZLUzJDajl76uNpiEcZ3',
          defaultMessage: 'From {count, plural, one {# day} other {# days}} before the booking date',
          description: 'Non-refundable period label in days in experience cancellation policy modal',
        },
        { count: Math.floor(duration.asDays()) },
      )
    }
    return intl.formatMessage(
      {
        id: '5Ds3M9INAJnZAdLeqBBm',
        defaultMessage: 'From {count, plural, one {# hour} other {# hours}} before the booking date',
        description: 'Non-refundable period label in hours in experience cancellation policy modal',
      },
      { count: Math.floor(hours) },
    )
  }, [policies, intl])

  return (
    <Modal
      title={intl.formatMessage({
        id: 'Favkksjxh0gzra5bNp-d',
        defaultMessage: 'Cancellation policy',
        description: 'Modal title for experience cancellation policy in the experiences section',
      })}
      size="L"
    >
      {cancellationInfo && (
        <>
          <BodyText className="full-width" variant="large">
            {cancellationInfo.text}
          </BodyText>
          {policies.length > 0 &&
            <PolicyGrid>
              {policies.map(([_, policies], index) => {
                const refundValue = Math.min(...policies.map(policy => policy.value))
                return (
                  <Fragment key={index}>
                    <VerticalSpacer gap={8}>
                      {policies.map(policy => (
                        <BodyText variant="medium" weight="bold" key={policy.id}>
                          {policy.periodLabel}
                        </BodyText>
                      ))}
                    </VerticalSpacer>
                    <Group direction="vertical" verticalAlign="center">
                      {refundValue > 0 && <BodyText variant="medium">
                        {intl.formatMessage(
                          {
                            id: 'YJmCVvx-VaJ_-St831rr',
                            defaultMessage: '{refundValue}% refund to your original payment method or credits.',
                            description: 'Refund percentage text in experience cancellation policy modal',
                          },
                          { refundValue },
                        )}
                      </BodyText>}
                      {refundValue === 0 && <BodyText variant="medium">
                        {intl.formatMessage({
                          id: 'IUAYulNzuNu8pyqZKiwt',
                          defaultMessage: 'Non-refundable',
                          description: 'Label for non-refundable policy in experience cancellation modal',
                        })}
                      </BodyText>}
                    </Group>
                    <Divider />
                  </Fragment>
                )
              })}
              <Fragment key={policies.length}>
                <BodyText variant="medium" weight="bold" className="format-propercase">
                  {nonRefundableLabel}
                </BodyText>
                <Group direction="vertical" verticalAlign="center">
                  <BodyText variant="medium">
                    {intl.formatMessage({
                      id: 'a7WB5ghrC1IQzeo4_zqZ',
                      defaultMessage: 'Non-refundable',
                      description: 'Default non-refundable label in experience cancellation modal',
                    })}
                  </BodyText>
                </Group>
              </Fragment>
            </PolicyGrid>}
        </>
      )}
      <HelpPane onSupportEmailClick={onSupportEmailClick} onSupportNumberClick={onSupportNumberClick} />
    </Modal>
  )
}

export default ExperienceCancellationPolicyModal
